import type { WorkItemDetail } from '@deskworks/shared-types';
import { useQuery } from '@tanstack/react-query';
import { Bot, Crown, NotebookPen, Plus, User } from 'lucide-react';
import { useState } from 'react';
import { Markdown } from '@/components/markdown';
import { RelativeTime } from '@/components/relative-time';
import { Section } from '@/components/section';
import { SessionLink } from '@/components/session-link';
import { EmptyState, ErrorState, LoadingRows } from '@/components/states';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { api } from '@/lib/api';
import { useApiMutation } from '@/lib/hooks';
import { t } from '@/lib/i18n';
import { qk } from '@/lib/query-keys';

/**
 * The item's work history (docs/features/work-items.md#work-history): what everyone who worked on it did, oldest first.
 * Every run of an agent or the Lead ends with an entry; a person adds what they did themselves.
 */
export function WorkHistorySection({ item }: { item: WorkItemDetail }) {
  const [text, setText] = useState('');
  const [adding, setAdding] = useState(false);
  const history = useQuery({ queryKey: qk.workItemHistory(item.id), queryFn: () => api.workItems.history(item.id) });
  const add = useApiMutation(() => api.workItems.addHistory(item.id, { summary: text }), {
    invalidate: [qk.workItemHistory(item.id)],
    onSuccess: () => {
      setText('');
      setAdding(false);
    },
  });
  if (item.kind === 'watch' || item.kind === 'report') return null;
  const entries = history.data ?? [];

  return (
    <Section
      icon={<NotebookPen />}
      title={t('Work history')}
      description={t('What everyone who worked on it did, step by step.')}
      actions={
        !adding && (
          <Button variant="ghost" size="xs" onClick={() => setAdding(true)} title={t('Write down what you did yourself, e.g. “Merged the MR by hand”')}>
            <Plus /> {t('Add')}
          </Button>
        )
      }
    >
      {history.isPending ? (
        <LoadingRows rows={2} />
      ) : history.isError ? (
        <ErrorState error={history.error} onRetry={() => void history.refetch()} />
      ) : !entries.length && !adding ? (
        <EmptyState compact title={t('Nothing done yet')} description={t('Each agent writes down here what it did when its run ends.')} />
      ) : (
        <ol className="relative flex flex-col gap-4 border-l pl-4">
          {entries.map((h) => (
            <li key={h.id} className="relative">
              <span className="absolute -left-[22.5px] top-0.5 flex size-3.5 items-center justify-center rounded-full bg-background">
                {h.authorType === 'user' ? (
                  <User className="size-3.5 text-emerald-600" />
                ) : h.byLead ? (
                  <Crown className="size-3.5 text-amber-600" />
                ) : (
                  <Bot className="size-3.5 text-indigo-600" />
                )}
              </span>
              <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                <span className="font-medium text-foreground/80">{h.authorName ?? (h.authorType === 'user' ? t('A person') : t('Agent'))}</span>
                {h.byLead && <span>{t('Lead')}</span>}
                <RelativeTime value={h.createdAt} />
                {h.executionId && <SessionLink executionId={h.executionId} workspaceId={item.workspaceId} />}
              </div>
              <div className="mt-1 text-sm">
                <Markdown text={h.summary} />
              </div>
              {h.refs.length > 0 && (
                <div className="mt-1.5 flex flex-wrap gap-1.5">
                  {h.refs.map((r) => (
                    <span key={`${r.type}:${r.id}`} className="rounded-full bg-muted px-2 py-0.5 font-mono text-[11px] text-muted-foreground">
                      {r.type.replace(/_/g, ' ')} {r.id}
                    </span>
                  ))}
                </div>
              )}
            </li>
          ))}
        </ol>
      )}
      {adding && (
        <form
          className="mt-3 flex flex-col gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (text.trim()) add.mutate();
          }}
        >
          <Textarea value={text} onChange={(e) => setText(e.target.value)} placeholder={t('What did you do? e.g. Called the customer, they confirm the fix')} rows={3} autoFocus />
          <div className="flex gap-2">
            <Button size="sm" type="submit" disabled={!text.trim()} loading={add.isPending}>
              {t('Add')}
            </Button>
            <Button size="sm" variant="outline" type="button" onClick={() => setAdding(false)}>
              {t('Cancel')}
            </Button>
          </div>
        </form>
      )}
    </Section>
  );
}
